import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useState, useCallback, useEffect } from 'react';
import { useDebounce } from './useDebounce';
import { findPathsToMatchingValues } from '../utils/searchUtils';
import { generateExpansionsFromPaths } from '../utils/expandPathUtils';
/**
 * Custom hook to handle state searching functionality
 * @param sortedState - Sorted state object to search through
 * @param setExpandedSections - Function to update expanded sections
 * @returns - Search handlers and state
 */
export var useStateSearch = function useStateSearch(sortedState, setExpandedSections) {
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    searchTerm = _useState2[0],
    setSearchTerm = _useState2[1];
  var _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    inputValue = _useState4[0],
    setInputValue = _useState4[1];
  var debouncedSearchTerm = useDebounce(searchTerm, 500);
  var _useState5 = useState({}),
    _useState6 = _slicedToArray(_useState5, 2),
    searchResults = _useState6[0],
    setSearchResults = _useState6[1];
  var _useState7 = useState([]),
    _useState8 = _slicedToArray(_useState7, 2),
    matchingPaths = _useState8[0],
    setMatchingPaths = _useState8[1];
  var _useState9 = useState(''),
    _useState0 = _slicedToArray(_useState9, 2),
    singleMatchPath = _useState0[0],
    setSingleMatchPath = _useState0[1];

  // Handle search input change immediately
  var handleSearchInputChange = useCallback(function (input) {
    var _input$target;
    // Handle both string input and change events
    var value = '';
    if (typeof input === 'string') {
      value = input;
    } else if ((input === null || input === void 0 || (_input$target = input.target) === null || _input$target === void 0 ? void 0 : _input$target.value) !== undefined) {
      value = input.target.value;
    }
    setInputValue(value);
    // Debounce the actual search processing
    setTimeout(function () {
      setSearchTerm(value);
    }, 0);
  }, []);

  // Clear search
  var clearSearch = useCallback(function () {
    setSearchTerm('');
    setInputValue('');
    setSearchResults({});
    setMatchingPaths([]);
    setSingleMatchPath('');
  }, []);

  // Parse search term for path navigation
  var parseSearchPath = useCallback(function (search) {
    return (search === null || search === void 0 ? void 0 : search.trim().toLowerCase()) || '';
  }, []);

  // Process search separately from input handling
  useEffect(function () {
    if (!debouncedSearchTerm) {
      setSearchResults({});
      setMatchingPaths([]);
      setSingleMatchPath('');
      return;
    }

    // Parse the search path
    var searchPath = parseSearchPath(debouncedSearchTerm);

    // Wrap in try/catch to prevent uncaught errors
    try {
      // Use requestAnimationFrame to process expansion during idle time
      requestAnimationFrame(function () {
        // If it's a path search (e.g. "app.extension")
        if (searchPath.includes('.')) {
          var pathSegments = searchPath.split('.');

          // Auto-expand the top-level key
          if (pathSegments.length > 0) {
            setExpandedSections(function (prev) {
              return _objectSpread(_objectSpread({}, prev), {}, _defineProperty({}, pathSegments[0], true));
            });

            // Set expanded state for nested segments - only exactly what was searched
            // For example, if search is "app.extension", expand only "extension" under "app"
            if (pathSegments.length > 1) {
              var expandedPaths = {};
              for (var i = 1; i < pathSegments.length; i++) {
                expandedPaths[pathSegments[i]] = true;
              }
              setSearchResults(expandedPaths);
            }
          }
          setSingleMatchPath('');
        } else {
          // For value search, safely find paths to matching values
          var paths = [];

          // Limit the number of sections processed for better performance
          var sectionEntries = Object.entries(sortedState).slice(0, 20);

          // Process each section of state to find paths to matching values
          sectionEntries.forEach(function (_ref) {
            var _ref2 = _slicedToArray(_ref, 2),
              key = _ref2[0],
              value = _ref2[1];
            try {
              // Use the optimized path finding function
              var sectionPaths = findPathsToMatchingValues(value, searchPath, key);
              if (sectionPaths && sectionPaths.length > 0) {
                // Add paths to the collection
                paths.push.apply(paths, _toConsumableArray(sectionPaths));

                // Auto-expand the top-level section containing matches
                setExpandedSections(function (prev) {
                  return _objectSpread(_objectSpread({}, prev), {}, _defineProperty({}, key, true));
                });
              }
            } catch (err) {
              console.error("Error processing section ".concat(key, ":"), err);
            }
          });

          // Store the matching paths (safely limit their number)
          var limitedPaths = paths.slice(0, 100);
          setMatchingPaths(limitedPaths);

          // Set the single match path if there's exactly one match
          if (paths.length === 1) {
            // Format the path to be more readable
            var formattedPath = paths[0].replace(/\.(\d+)\./g, '[$1].');
            setSingleMatchPath(formattedPath);
          } else {
            setSingleMatchPath('');
          }

          // Generate expanded paths from full paths to matches
          var newExpandedPaths = generateExpansionsFromPaths(limitedPaths);
          setSearchResults(newExpandedPaths);
        }
      });
    } catch (err) {
      console.error('Error in search processing:', err);
    }
  }, [debouncedSearchTerm, parseSearchPath, sortedState, setExpandedSections]);
  return {
    searchTerm: searchTerm,
    inputValue: inputValue,
    debouncedSearchTerm: debouncedSearchTerm,
    searchResults: searchResults,
    matchingPaths: matchingPaths,
    singleMatchPath: singleMatchPath,
    handleSearchInputChange: handleSearchInputChange,
    clearSearch: clearSearch,
    parseSearchPath: parseSearchPath
  };
};